import {
  maximumAllowedMonitoredRolloutDurationMs,
  maximumAllowedRolloutStageDurationMs,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { getDurationFromMs } from '@gonfalon/format/getDisplayDurationFromMs';
import { fromJS, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import { msg } from '@gonfalon/messages/msg';
import { formatDuration } from 'date-fns';
import { isNil } from 'lodash';
import { v4 } from 'uuid';

import { guardedReleasePhaseConfigurationStepsSchema } from '#components/automated-rollouts/shared/schemas/releasePhaseStepsSchema';
import type { VariationType } from '#utils/flags/flagConstants';
import type { Fallthrough } from '#utils/flags/records/Fallthrough';
import type { Prerequisite } from '#utils/flags/records/Prerequisite';
import type { ReleasePhaseConfig } from '#utils/flags/records/ReleasePhaseConfig';
import type { Rollout } from '#utils/flags/records/Rollout';
import type { Target } from '#utils/flags/records/Target';
import type { Variation } from '#utils/flags/records/Variation';
import type { WeightedVariation } from '#utils/flags/weightedVariationUtils';

import { max, min, required } from './errors';

export const variationNotFound = (variation: number) =>
  fromJS({
    type: 'variationNotFound',
    variation,
  });

export const variationOrRollout = (variation?: number, rollout?: Rollout) =>
  fromJS({
    type: 'variationOrRollout',
    variation,
    rollout,
  });

export const variationTypeMismatch = (variations: List<Variation>, variationType?: VariationType) =>
  fromJS({
    type: 'variationTypeMismatch',
    variations,
    variationType,
  });

export const variationDuplicateNames = (variations: List<Variation>, duplicates: List<Variation>) =>
  fromJS({
    type: 'variationDuplicateNames',
    variations,
    duplicates,
  });

export const variationDuplicateValues = (variations: List<Variation>, duplicates: List<Variation>) =>
  fromJS({
    type: 'variationDuplicateValues',
    variations,
    duplicates,
  });

export const invalidAttribute = (name: string) =>
  fromJS({
    type: 'invalidAttribute',
    name,
  });

type RolloutValidationProps = {
  // oxlint-disable-next-line typescript/no-explicit-any
  bucketBy: Map<any, any>;
  // oxlint-disable-next-line typescript/no-explicit-any
  variations: Map<any, WeightedVariationValidation>;
  // oxlint-disable-next-line typescript/no-explicit-any
  root: Map<any, any>;
};

export class RolloutValidation extends Record<RolloutValidationProps>({
  bucketBy: Map(),
  variations: Map(),
  root: Map(),
}) {
  isEmpty() {
    return this.variations.isEmpty() && this.root.isEmpty() && this.bucketBy.isEmpty();
  }

  isInvalid() {
    return (
      this.variations.some((v) => v.isInvalid()) ||
      this.getIn(['root', 'type']) === 'max' ||
      this.getIn(['root', 'type']) === 'min' ||
      this.getIn(['root', 'type']) === 'variationNotFound' ||
      this.getIn(['bucketBy', 'type']) === 'invalidAttribute'
    );
  }

  isIncomplete() {
    return this.variations.some((v) => v.isIncomplete());
  }

  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.isInvalid()) {
      if (this.getIn(['bucketBy', 'type']) === 'invalidAttribute') {
        const attributeName = this.getIn(['bucketBy', 'name']);
        return msg('targeting.rolloutValidation.invalidBucketBy', { attributeName });
      }
    }
  }
}

interface GuardedReleasePhaseConfigValidationProps {
  /* oxlint-disable typescript/no-explicit-any */
  randomizationUnit: Map<string, any>;
  monitoringWindowMilliseconds: Map<string, any>;
  rolloutWeight: Map<string, any>;
  /* oxlint-enable typescript/no-explicit-any */
  stages?: List<{
    rolloutWeight: number;
    monitoringWindowMilliseconds: number;
  }>;
  metrics?: List<{
    metricKey: string;
    regressionThreshold: number;
    onRegression: {
      notify: boolean;
      rollback: boolean;
    };
  }>;
  metricSources?: List<{
    key: string;
    isGroup: boolean;
  }>;
}

export class GuardedReleasePhaseConfigValidation extends Record<GuardedReleasePhaseConfigValidationProps>({
  randomizationUnit: Map(),
  monitoringWindowMilliseconds: Map(),
  rolloutWeight: Map(),
}) {
  isEmpty() {
    return (
      this.randomizationUnit.isEmpty() &&
      this.monitoringWindowMilliseconds.isEmpty() &&
      this.rolloutWeight.isEmpty() &&
      (!this.stages || this.stages.isEmpty()) &&
      (!this.metrics || this.metrics.isEmpty())
    );
  }

  isInvalid() {
    return (
      this.monitoringWindowMilliseconds.get('type') === 'max' ||
      this.monitoringWindowMilliseconds.get('type') === 'min' ||
      this.rolloutWeight.get('type') === 'max' ||
      this.rolloutWeight.get('type') === 'min' ||
      this.rolloutWeight.get('type') === 'invalidAttribute'
    );
  }

  isIncomplete() {
    return this.randomizationUnit.get('type') === 'required';
  }

  /* TODO: Add V2 validation messages in a follow up PR */
  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.isInvalid()) {
      if (this.monitoringWindowMilliseconds.get('type') === 'max') {
        const d = getDurationFromMs(this.monitoringWindowMilliseconds.get('max'));
        const dateFnsKey = ({ day: 'days', hour: 'hours', minute: 'minutes' } as const)[d.unit];
        const duration = formatDuration({ [dateFnsKey]: d.quantity });
        return msg('guardedRollouts.validation.monitoringWindowMax', { duration });
      }

      if (this.monitoringWindowMilliseconds.get('type') === 'min') {
        return msg('guardedRollouts.validation.monitoringWindowMin');
      }

      if (this.rolloutWeight.get('type') === 'max') {
        const value = this.rolloutWeight.get('max');
        return msg('guardedRollouts.validation.trafficMax', { percent: value / 1000 });
      }

      if (this.rolloutWeight.get('type') === 'min') {
        return msg('guardedRollouts.validation.trafficMin');
      }
    }

    if (this.isIncomplete()) {
      if (this.randomizationUnit.get('type') === 'required') {
        return msg('guardedRollouts.validation.randomizationUnitRequired');
      }
    }
  }
}

export class WeightedVariationValidation extends Record({
  variation: Map(),
  weight: Map(),
}) {
  isEmpty() {
    return this.variation.isEmpty() && this.weight.isEmpty();
  }

  isInvalid() {
    return this.getIn(['weight', 'type']) === 'min' || this.getIn(['weight', 'type']) === 'max';
  }

  isIncomplete() {
    return this.getIn(['variation', 'type']) === 'required';
  }

  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.isInvalid()) {
      if (this.getIn(['weight', 'type']) === 'min') {
        const value = this.getIn(['weight', 'min']);
        return msg('targeting.rolloutValidation.weightAtLeast', { value });
      }

      if (this.getIn(['weight', 'type']) === 'max') {
        const value = this.getIn(['weight', 'max']);
        return msg('targeting.rolloutValidation.weightAtMost', { value });
      }
    }

    if (this.isIncomplete()) {
      return msg('targeting.rolloutValidation.rolloutRequired');
    }
  }
}

export class PrerequisiteValidation extends Record({
  key: null,
}) {
  isEmpty() {
    return !this.key;
  }

  isInvalid() {
    return (
      !this.isEmpty() &&
      (this.getIn(['key', 'type']) === 'unavailable' || this.getIn(['key', 'type']) === 'variationNotFound')
    );
  }

  isIncomplete() {
    return !this.isEmpty() && this.getIn(['key', 'type']) === 'required';
  }

  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.isInvalid()) {
      if (this.getIn(['key', 'type']) === 'unavailable') {
        return msg('flags.prerequisiteValidation.flagUnavailable');
      }

      if (this.getIn(['key', 'type']) === 'variationNotFound') {
        return msg('flags.prerequisiteValidation.variationNotFound');
      }
    }

    if (this.isIncomplete()) {
      return msg('flags.prerequisiteValidation.flagRequired');
    }
  }
}

export class UserTargetsValidation extends Record({
  userTargets: List(),
  alertThreshold: -1,
  warningThreshold: -1,
}) {
  isEmpty() {
    return !this.userTargets || this.getNumberTargets() === 0;
  }

  enforceWarning() {
    return this.warningThreshold >= 0;
  }

  enforceAlert() {
    return this.alertThreshold >= 0;
  }

  getNumberTargets() {
    return this.userTargets.reduce((sum, target) => sum + parseInt(target.values.size, 10), 0);
  }

  hasTooManyTargetsAlert() {
    return this.enforceAlert() ? !this.isEmpty() && this.getNumberTargets() > this.alertThreshold : false;
  }

  isAlert() {
    return this.enforceAlert() ? this.hasTooManyTargetsAlert() : false;
  }

  isWarning() {
    return this.enforceWarning()
      ? !this.isEmpty() &&
          (!this.enforceAlert() || this.getNumberTargets() <= this.alertThreshold) &&
          this.getNumberTargets() > this.warningThreshold
      : false;
  }
}

export class FallthroughValidation extends Record({
  root: Map(),
  rollout: new RolloutValidation(),
  measuredRolloutConfig: new GuardedReleasePhaseConfigValidation(),
}) {
  isEmpty() {
    return this.root.isEmpty() && this.rollout.isEmpty() && this.measuredRolloutConfig.isEmpty();
  }

  isInvalid() {
    return (
      !this.isEmpty() &&
      (this.rollout.isInvalid() ||
        this.measuredRolloutConfig.isInvalid() ||
        this.getIn(['root', 'type']) === 'variationNotFound')
    );
  }

  isIncomplete() {
    return (
      !this.isEmpty() &&
      (this.rollout.isIncomplete() ||
        this.measuredRolloutConfig.isIncomplete() ||
        this.getIn(['root', 'type']) === 'variationOrRollout')
    );
  }

  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.isInvalid()) {
      if (this.getIn(['root', 'type']) === 'variationNotFound') {
        return msg('targeting.ruleValidation.invalidVariation');
      }

      if (this.getIn(['rollout', 'root', 'type']) === 'variationNotFound') {
        return msg('targeting.ruleValidation.invalidRolloutVariation');
      }

      if (this.measuredRolloutConfig.isInvalid()) {
        return this.measuredRolloutConfig.getMessage();
      }
    }

    if (this.isIncomplete()) {
      if (this.getIn(['root', 'type']) === 'variationOrRollout') {
        return msg('targeting.ruleValidation.variationOrRolloutRequired');
      }

      if (this.measuredRolloutConfig.isIncomplete()) {
        return this.measuredRolloutConfig.getMessage();
      }
    }
  }
}

export function validatePrerequisite(prereq: Prerequisite) {
  let validation = new PrerequisiteValidation();

  if (prereq.key === '') {
    validation = validation.set('key', required(prereq.key));
  }

  return validation;
}

export type ThresholdsType = {
  userTargetAlertThreshold?: number;
  userTargetWarningThreshold?: number;
};

export function validateUserTargets(userTargets: List<Target>, thresholds: ThresholdsType = {}) {
  return new UserTargetsValidation({
    userTargets,
    alertThreshold: thresholds.userTargetAlertThreshold,
    warningThreshold: thresholds.userTargetWarningThreshold,
  });
}

export function validateWeightedVariation(wv: WeightedVariation, variations: List<Variation>) {
  let validation = new WeightedVariationValidation();

  if (isNil(wv.variation)) {
    validation = validation.set('variation', required(wv.variation));
  }

  if (isFinite(wv.weight) && wv.weight < 0) {
    validation = validation.set('weight', min(0, wv.weight));
  }

  if (variations && !variations.get(wv.variation)) {
    validation = validation.set('variation', variationNotFound(wv.variation));
  }

  return validation;
}

export function validateRollout(rollout: Rollout, variations?: List<Variation>, weightTotal?: number) {
  let validation = new RolloutValidation();

  const total = weightTotal || rollout.variations.reduce((sum, wv) => (!wv._untracked ? sum + wv.weight : sum + 0), 0);

  if (total > 100000) {
    validation = validation.set('root', max(100000, total));
  }

  if (total < 100000 && !rollout.experimentAllocation) {
    validation = validation.set('root', min(100000, total));
  }

  if (rollout.experimentAllocation && total < 0) {
    validation = validation.set('root', min(0, total));
  }

  if (rollout.bucketBy === 'secondary') {
    validation = validation.set('bucketBy', invalidAttribute(rollout.bucketBy));
  }

  for (let i = 0; i < rollout.variations.size; i++) {
    const wv = rollout.variations.get(i);
    if (wv && variations) {
      const result = validateWeightedVariation(wv, variations);
      if (!result.isEmpty()) {
        if (result.getIn(['variation', 'type']) === 'variationNotFound') {
          validation = validation.set('root', result.variation);
        } else {
          validation = validation.setIn(['variations', i], result);
        }
      }
    }
  }

  return validation;
}

export function validateGuardedReleasePhaseConfig(config: ReleasePhaseConfig) {
  let validation = new GuardedReleasePhaseConfigValidation();
  const maxDuration = maximumAllowedMonitoredRolloutDurationMs();

  if (!config.randomizationUnit) {
    validation = validation.set('randomizationUnit', required(config.randomizationUnit));
  }

  if (maxDuration > 0 && config.monitoringWindowMilliseconds > maxDuration) {
    validation = validation.set('monitoringWindowMilliseconds', max(maxDuration, config.monitoringWindowMilliseconds));
  }

  if (config.monitoringWindowMilliseconds < 1) {
    validation = validation.set('monitoringWindowMilliseconds', min(0, config.monitoringWindowMilliseconds));
  }

  if (config.rolloutWeight > 50000) {
    validation = validation.set('rolloutWeight', max(50000, config.rolloutWeight));
  }

  if (config.rolloutWeight < 1000) {
    validation = validation.set('rolloutWeight', min(1, config.rolloutWeight));
  }

  if (config.stages && config.stages.isEmpty()) {
    validation = validation.set('stages', required(config.stages));
  }

  if (
    config.stages &&
    config.stages.some((stage) => stage.rolloutWeight < 1 || stage.monitoringWindowMilliseconds < 1)
  ) {
    validation = validation.set('stages', invalidAttribute('stages'));
  }

  if (config.stages && !config.stages.isEmpty()) {
    const progressiveReleasePhaseSteps = convertGuardedReleasePhaseConfigToProgressiveStepsState(config).steps;
    const isValid = guardedReleasePhaseConfigurationStepsSchema.safeParse(progressiveReleasePhaseSteps).success;
    if (!isValid) {
      validation = validation.set('rolloutWeight', invalidAttribute('stages'));
    }

    const maxStageMonitoringWindow = config.stages.maxBy(
      (stage) => stage.monitoringWindowMilliseconds,
    )?.monitoringWindowMilliseconds;

    const minStageMonitoringWindow = config.stages.minBy(
      (stage) => stage.monitoringWindowMilliseconds,
    )?.monitoringWindowMilliseconds;
    const minStageRolloutWeight = config.stages.minBy((stage) => stage.rolloutWeight)?.rolloutWeight;

    if (minStageRolloutWeight && minStageRolloutWeight < 1) {
      validation = validation.setIn(['stages', 'rolloutWeight'], min(1, minStageRolloutWeight));
    }

    if (minStageMonitoringWindow && minStageMonitoringWindow < 1) {
      validation = validation.setIn(['stages', 'monitoringWindowMilliseconds'], min(1, minStageMonitoringWindow));
    }

    const maxStageDuration = maximumAllowedRolloutStageDurationMs();
    if (maxStageMonitoringWindow && maxStageDuration > 0 && maxStageMonitoringWindow > maxStageDuration) {
      validation = validation.setIn(
        ['stages', 'monitoringWindowMilliseconds'],
        max(maxStageDuration, maxStageMonitoringWindow),
      );
    }
  }

  if (config.metrics && !config.metrics.isEmpty()) {
    if (
      config.metrics.some(
        (metric) => metric.onRegression && (metric.onRegression.notify || metric.onRegression.rollback),
      )
    ) {
      validation = validation.set('metrics', invalidAttribute('metrics'));
    }

    if (config.metrics.some((metric) => metric.metricKey === '')) {
      validation = validation.set('metrics', invalidAttribute('metrics'));
    }
  }

  return validation;
}

// this enables us to use zod to validate that the guarded rollout stages conform to the schema, mostly for rollouts with custom durations
function convertGuardedReleasePhaseConfigToProgressiveStepsState(config: ReleasePhaseConfig) {
  return {
    steps: config.stages
      ?.toArray()
      .filter((stage) => stage.rolloutWeight !== undefined && stage.monitoringWindowMilliseconds !== undefined)
      .map(({ rolloutWeight, monitoringWindowMilliseconds }) => ({
        key: v4(),
        rolloutWeight,
        duration: getDurationFromMs(monitoringWindowMilliseconds),
      }))
      // we don't want to include the final step to rollout to 100% in the builder because that's a step you can't configure
      // the config validation schema will fail if we include this
      .filter(({ rolloutWeight }) => rolloutWeight !== 100000) ?? [
      { key: v4(), rolloutWeight: 10000, duration: { quantity: 1, unit: 'day' } },
    ],
  };
}

export function validateFallthrough(fallthrough: Fallthrough, variations: List<Variation>) {
  let validation = new FallthroughValidation();

  if (
    (isNil(fallthrough.variation) && isNil(fallthrough.rollout)) ||
    (!isNil(fallthrough.variation) && !isNil(fallthrough.rollout))
  ) {
    validation = validation.set('root', variationOrRollout(fallthrough.variation, fallthrough.rollout));
  }

  if (fallthrough.variation && !variations.get(fallthrough.variation)) {
    validation = validation.set('root', variationNotFound(fallthrough.variation));
  }

  if (fallthrough.rollout) {
    const result = validateRollout(fallthrough.rollout, variations, undefined);
    if (!result.isEmpty()) {
      validation = validation.set('rollout', result);
    }
  }

  if (fallthrough.measuredRolloutConfig) {
    const result = validateGuardedReleasePhaseConfig(fallthrough.measuredRolloutConfig);

    if (!result.isEmpty()) {
      validation = validation.set('measuredRolloutConfig', result);
    }
  }

  return validation;
}

export function validateVariation(variation: Variation) {
  let errors = Map();

  const value = variation.value;
  if (isNil(value) || Number.isNaN(value)) {
    errors = errors.set('value', required(value));
  }

  return errors;
}
